import React from "react";
import {Tag} from "antd";
import moment from "moment";
import {renderAmount} from "../../../helpers/renderAmount";

/**
 * Gemeinsame Helfer fuer die Vorher/Nachher-Darstellung in History-Modals
 * (Course-Historie und Buchungs-Historie). Die Funktionen sind bewusst
 * Parameter-basiert gehalten, damit sie ohne React-Context wiederverwendbar
 * sind.
 *
 * Entstanden durch Extraktion aus
 * `src/client/containers/courseHistory/CourseHistoryContent.tsx`
 * (Stand: B-223 lessonBooking-deleted-payments). Die Defaults entsprechen
 * 1:1 dem urspruenglichen Verhalten der Course-Historie - Booking-Historie
 * erweitert die Listen ueber {@link HistoryRenderOptions}.
 */

/**
 * Default-Liste der Felder, die als Geldbetraege formatiert werden sollen.
 * Reihenfolge / Inhalt entsprechen dem Original aus CourseHistoryContent.tsx.
 */
export const MONEY_FIELDS: ReadonlyArray<string> = [
    "grossPrice",
    "netPrice",
    "discount",
    "installmentAmount",
    "price",
    "amount",
    "includedEntranceFee",
];

/**
 * Default-Liste der Booking-spezifischen Geldfelder. Wird in der
 * Booking-Historie zusaetzlich zur Default-Liste verwendet.
 */
export const BOOKING_MONEY_FIELDS: ReadonlyArray<string> = [
    "paymentAmount",
    "memberDiscount",
    "creditedBalance",
    "totalPaymentAmount",
    "bookingGrossPrice",
    "bookingNetPrice",
];

/**
 * Felder die Prozentwerte (als Dezimalzahl 0.19 = 19%) enthalten.
 */
export const VAT_FIELDS: ReadonlyArray<string> = ["vat"];

/**
 * Default-Liste der Felder, die in der Detail-Ansicht ausgeblendet werden.
 * Reihenfolge / Inhalt entsprechen dem Original aus CourseHistoryContent.tsx.
 */
export const HIDDEN_FIELDS: ReadonlyArray<string> = ["id", "customCourseNumberPrefix"];

/**
 * Booking-spezifische technische Felder, die in oldValues/newValues haeufig
 * vorkommen, fuer den Nutzer aber wenig Mehrwert haben.
 *
 * `totalPaymentAmount` wurde aktiv aus den Snapshots entfernt (siehe
 * `bookingHistory.service.ts`), bleibt aber gefiltert, damit Alt-Eintraege
 * nicht mehr "Summe Zahlungen" anzeigen — der Wert war redundant zum neuen
 * Bruttopreis und im Modal-Titel ohnehin enthalten.
 */
export const BOOKING_HIDDEN_FIELDS: ReadonlyArray<string> = [
    "id",
    "paymentMethodId",
    "scheduledPaymentId",
    "bookingId",
    "courseId",
    "attendeeId",
    "bookerId",
    "totalPaymentAmount",
    // `overrideCleared` ist ein Marker-Flag, das vom Backend in den Snapshot
    // geschrieben wird, wenn `clearManuallyAdjusted` ausgeloest wurde.
    // Es steuert das Frontend-Layout (Card-Titel + Accent), soll aber nicht
    // als generisches "true/false"-Feld in der Diff-Liste gerendert werden.
    "overrideCleared",
    // Bewusst ausgeblendet im Booking-History-Modal: Nettopreis ergibt sich
    // aus dem bereits sichtbaren Bruttopreis und ist hier redundant. Die
    // Course-Historie nutzt diese Liste nicht und zeigt netPrice weiterhin.
    "netPrice",
    "bookingNetPrice",
    // Zahlungsart-Felder werden in den Posten-Cards (ScheduledPaymentsCard)
    // bereits pro Posten ausgegeben. Im generischen Diff erscheinen sie
    // teils ohne Wert und stiften mehr Verwirrung als Erkenntnis.
    "paymentMethod",
    "paymentMethodTypeName",
    "paymentMethodType",
    "paymentType",
    // Rabatt-Name ist im Modal-Titel und in der Posten-Liste nicht
    // sichtbar relevant — wenn ein Rabatt aktiv ist, zaehlt der Wert,
    // nicht der interne Name.
    "discountName",
    // CourseLessonBooking-Snapshots (kompletter Junction-Datensatz) werden
    // bei `logBookingChange` mit allen Sub-Feldern serialisiert. Die fuer
    // den Nutzer relevanten Aenderungen sind bereits in der Posten-Liste
    // (ScheduledPaymentsCard) und im Description-Header erfasst — der
    // generische Diff dieser Arrays/Sub-Objekte produziert nur visuelles
    // Rauschen (UUIDs, Metadaten, verschachtelte Lesson-Daten).
    "courseLessonBookings",
    "courseLessons",
    "courseLesson",
    "courseLessonId",
    "addedBookings",
    "removedBookings",
    "addedLessonIds",
    "removedLessonIds",
    // CourseLessonBooking-Sub-Felder: werden hier defensiv ergaenzt, damit
    // sie auch dann nicht erscheinen, wenn ein Snapshot nur ein einzelnes
    // CourseLessonBooking-Objekt direkt als oldValues/newValues schreibt.
    "bookingType",
    "bookingDate",
    "attendance",
    "note",
    "instructorInvoiceId",
    "billingAmount",
    "billingDate",
    // Allgemeine Audit-/Multi-Tenant-Metadaten haben fuer den Nutzer keinen
    // Mehrwert — Datum + User stehen bereits im Modal-Header.
    "createDate",
    "updateDate",
    "createdByUserId",
    "updatedByUserId",
    "koOrganizationId",
    // Nachvollziehbarkeit beim Umbuchen: Der strukturierte Übertrags-Kontext
    // wird vom dedizierten Transfer-Alert (BookingHistoryDetails) gerendert,
    // nicht als generisches JSON-Diff-Feld.
    "transferContext",
];

/**
 * Uebersetzungen fuer PaymentType Enum-Werte.
 */
export const PAYMENT_TYPE_LABELS: Record<string, string> = {
    Monthly: "Monatlich",
    PrePaid: "Vorauszahlung",
};

/**
 * Labels fuer bekannte Feldnamen (deutsche Uebersetzung).
 * Hinweis: Course-Felder sollten mit den fieldLabels im Backend
 * (courseHistory.service.ts) synchron gehalten werden.
 */
export const FIELD_LABELS: Record<string, string> = {
    // Preise (Course)
    grossPrice: "Bruttopreis",
    netPrice: "Nettopreis",
    vat: "MwSt",
    discount: "Rabatt",
    discountUnit: "Rabatteinheit",
    isSpecial: "Spezialpreis",
    includedEntranceFee: "Inkl. Eintritt",

    // Ratenzahlungen
    installmentAmount: "Ratenbetrag",
    installmentDue: "Fälligkeit",
    installmentType: "Ratentyp",

    // Mahnwesen (Freitext der Historieneinträge)
    withdrawnReason: "Grund der Rücknahme",
    dunningPausedNote: "Notiz zur Mahnsperre",

    // Datum & Zeit
    startDateTime: "Startzeit",
    endDateTime: "Endzeit",
    cycle: "Rhythmus",

    // Standort & Raum
    locationId: "Standort",
    venueId: "Raum",

    // Kurs-Grunddaten
    courseTypeId: "Kursart",
    courseNumber: "Kursnummer",
    prefixedCourseNumber: "Kursnummer",
    customCourseNumberPrefix: "Kursnummern-Prefix",
    courseNumberFormat: "Kursnummern-Format",

    // Teilnehmer
    maxAttendees: "Max. Teilnehmer",
    minAttendees: "Min. Teilnehmer",
    maxWaitlist: "Wartelistenplätze",

    // Sichtbarkeit & Status
    isActive: "Aktiv",
    showInWeb: "Im Web sichtbar",
    isArchived: "Archiviert",
    visibility: "Sichtbarkeit",

    // Buchungsbeschränkung (#1083, Schlüssel wie in server/bookingRestriction/bookingRestrictionDiff.ts)
    restrictionActive: "Beschränkung aktiv",
    restrictionVisibleForAll: "In der öffentlichen Kursliste anzeigen",
    restrictionBlockedPlaces: "Blockierte Plätze",
    restrictionStartDateTime: "Beschränkung Start",
    restrictionEndDateTime: "Beschränkung Ende",
    restrictionIncludeWaitingList: "Warteliste berücksichtigen",
    restrictionIncludeBookingList: "Buchungen berücksichtigen",
    restrictionMembersOnly: "Buchbarkeit auf Mitglieder begrenzen",
    restrictionAllowWaitlistWithoutPrerequisite: "Warteliste auch ohne erfüllte Voraussetzung erlauben",
    restrictionExpireAction: "Aktion nach Ablauf des Zeitraums",
    restrictionPrerequisiteCourses: "Freigabekurse",
    blockBookingOnline: "Online-Buchungen blockieren",

    // Beschreibungen & Infos
    annotation: "Anmerkung",
    websiteInfo: "Website-Info",
    additionalShortInfo: "Kurzinfo",

    // Zahlungsoptionen
    paymentType: "Zahlungsart",
    paymentInterval: "Kurspreisberechnung",
    allowMonthlyPayment: "Monatliche Zahlung für Kunden",
    allowUpFrontPayment: "Vorkasse für Kunden",
    monthlySchedule: "Monatlicher Zahlungsplan",
    dateOfFirstInstallment: "Zahldatum der ersten Monatsrate",

    // Buchungsoptionen
    allowLessonBooking: "Einzelstunden buchbar",

    // Listen/Arrays
    prices: "Preise",
    lessons: "Kurseinheiten",
    rates: "Raten",
    instructorIds: "Kursleiter-IDs",

    // Booking-spezifisch
    paymentAmount: "Betrag",
    paymentDue: "Fällig am",
    paymentMethod: "Zahlungsart",
    paymentMethodTypeName: "Zahlungsart",
    paymentMethodType: "Zahlungstyp",
    discountName: "Rabatt-Name",
    memberDiscount: "Mitgliederrabatt",
    creditedBalance: "Verrechnetes Guthaben",
    totalPaymentAmount: "Summe Zahlungen",
    bookingGrossPrice: "Buchungs-Bruttopreis",
    bookingNetPrice: "Buchungs-Nettopreis",
    scheduledPayments: "Posten",
    attendance: "Anwesenheit",
    canceled: "Storniert",
    paid: "Bezahlt",
    note: "Notiz",
};

/**
 * Optionen fuer die Render-Helfer. Wir uebergeben diese explizit, damit die
 * Helfer Context-frei sind und ueberall im Code verwendet werden koennen.
 */
export type HistoryRenderOptions = {
    /**
     * Waehrungs-Settings fuer die Geldbetragsformatierung.
     * `short` ist der Currency-Code (z.B. "EUR"), `symbol` das Symbol ("€").
     */
    currencySettings: {short: string; symbol: string};
    /**
     * Optional: Resolver fuer UUID-Felder zu lesbaren Namen
     * (z.B. courseTypeId -> "Schwimmkurs"). Nur fuer Course-Historie genutzt.
     * Wenn nicht uebergeben, wird die UUID gekuerzt angezeigt.
     */
    resolveUuid?: (fieldName: string, uuid: string) => string;
    /**
     * Optional: Whitelist fuer UUID-Felder, die ueber `resolveUuid` aufgeloest
     * werden sollen. Booking-Historie nutzt das nicht.
     */
    uuidLookupFields?: ReadonlyArray<string>;
    /**
     * Optional: Ueberschreibt die Default-Liste der Geldfelder.
     * Default = {@link MONEY_FIELDS}.
     */
    moneyFields?: ReadonlyArray<string>;
    /**
     * Optional: Ueberschreibt die Default-Liste der ausgeblendeten Felder.
     * Default = {@link HIDDEN_FIELDS}.
     */
    hiddenFields?: ReadonlyArray<string>;
    /**
     * Optional: Ueberschreibt / erweitert die Default-Field-Labels
     * fuer die Anzeige. Wird mit den Defaults gemerged (Defaults gewinnen
     * NICHT - das uebergebene Mapping hat Vorrang).
     */
    fieldLabels?: Record<string, string>;
};

/**
 * Formatiert einen Wert basierend auf dem Feldnamen.
 * Liefert `null` zurueck, wenn keine spezielle Formatierung greift -
 * Aufrufer fallen dann auf das Default-Rendering in {@link renderJsonValue}
 * zurueck.
 */
export const formatValueByFieldName = (
    fieldName: string,
    value: unknown,
    options: HistoryRenderOptions,
): React.ReactNode => {
    const {currencySettings, resolveUuid, uuidLookupFields} = options;
    const moneyFields = options.moneyFields ?? MONEY_FIELDS;

    // UUID-Felder aufloesen (courseTypeId, locationId, venueId)
    if (uuidLookupFields && uuidLookupFields.indexOf(fieldName) !== -1 && typeof value === "string") {
        if (resolveUuid) {
            const resolvedName = resolveUuid(fieldName, value);
            // Wenn aufgeloest, zeige den Namen; wenn nicht, zeige gekuerzte UUID
            if (resolvedName !== value) {
                return <span>{resolvedName}</span>;
            }
        }
        // UUID nicht aufloesbar - kuerze sie fuer bessere Lesbarkeit
        return <span title={value}>{value.substring(0, 8)}...</span>;
    }

    // Geldbetraege formatieren
    if (moneyFields.indexOf(fieldName) !== -1 && typeof value === "number") {
        return (
            <span>
                {renderAmount({
                    amount: value,
                    currency: currencySettings.short as "EUR" | "CHF" | "USD" | "GBP",
                })}
            </span>
        );
    }

    // MwSt/VAT als Prozent formatieren (0.19 -> 19%)
    if (VAT_FIELDS.indexOf(fieldName) !== -1 && typeof value === "number") {
        const percentValue = Math.round(value * 100);
        return <span>{percentValue}%</span>;
    }

    // Rabatteinheit
    if (fieldName === "discountUnit") {
        if (value === "Percentage") {
            return <span>Prozent (%)</span>;
        }
        if (value === "Currency") {
            return <span>Währung ({currencySettings.symbol})</span>;
        }
    }

    // PaymentType uebersetzen
    if (fieldName === "paymentType" && typeof value === "string") {
        const translatedValue = PAYMENT_TYPE_LABELS[value];
        if (translatedValue) {
            return <span>{translatedValue}</span>;
        }
    }

    // PaymentInterval uebersetzen (Kurspreisberechnung)
    if (fieldName === "paymentInterval" && typeof value === "string") {
        if (value === "PerCourse") {
            return <span>pro Kurs</span>;
        }
        if (value === "PerLesson") {
            return <span>pro Kurseinheit</span>;
        }
    }

    return null; // Kein spezielles Format
};

/**
 * Generischer Renderer fuer beliebige JSON-Werte aus oldValues/newValues.
 * Verzweigt nach Datentyp und nutzt {@link formatValueByFieldName} fuer
 * feldspezifische Formatierung.
 */
export const renderJsonValue = (
    value: unknown,
    options: HistoryRenderOptions,
    depth = 0,
    fieldName?: string,
): React.ReactNode => {
    if (value === null || value === undefined) {
        return <span style={{color: "#999"}}>-</span>;
    }

    // Versuche spezielle Formatierung basierend auf Feldnamen
    if (fieldName) {
        const formatted = formatValueByFieldName(fieldName, value, options);

        if (formatted) {
            return formatted;
        }
    }

    if (typeof value === "boolean") {
        return <Tag color={value ? "green" : "red"}>{value ? "Ja" : "Nein"}</Tag>;
    }

    if (typeof value === "number") {
        return <span>{value}</span>;
    }

    if (typeof value === "string") {
        // Pruefe ob es ein ISO-Datum ist
        if (/^\d{4}-\d{2}-\d{2}T/.test(value)) {
            return <span>{moment(value).format("DD.MM.YYYY HH:mm")}</span>;
        }

        return <span>{value}</span>;
    }

    if (Array.isArray(value)) {
        if (value.length === 0) {
            return <span style={{color: "#999"}}>[]</span>;
        }

        return (
            <ul style={{margin: 0, paddingLeft: depth > 0 ? 16 : 0, listStyle: "none"}}>
                {value.map((v, i) => (
                    <li key={i}>{renderJsonValue(v, options, depth + 1)}</li>
                ))}
            </ul>
        );
    }

    if (typeof value === "object") {
        const hiddenFields = options.hiddenFields ?? HIDDEN_FIELDS;
        const fieldLabels = options.fieldLabels ?? FIELD_LABELS;

        // Definiere die gewuenschte Feld-Reihenfolge (wichtige Felder zuerst)
        const fieldOrder = ["startDateTime", "endDateTime", "locationId", "venueId"];

        const entries = Object.entries(value as Record<string, unknown>)
            // Filtere technische Felder wie "id" heraus
            .filter(([k]) => hiddenFields.indexOf(k) === -1)
            // Sortiere nach definierter Reihenfolge (startDateTime vor endDateTime)
            .sort(([keyA], [keyB]) => {
                const indexA = fieldOrder.indexOf(keyA);
                const indexB = fieldOrder.indexOf(keyB);

                // Felder mit definierter Reihenfolge kommen zuerst
                if (indexA !== -1 && indexB !== -1) {
                    return indexA - indexB;
                }
                if (indexA !== -1) {
                    return -1;
                }
                if (indexB !== -1) {
                    return 1;
                }

                // Restliche Felder alphabetisch
                return keyA.localeCompare(keyB);
            });

        return (
            <div style={{marginLeft: depth > 0 ? 16 : 0}}>
                {entries.map(([k, v]) => {
                    const label = fieldLabels[k] || k;

                    return (
                        <div key={k} style={{marginBottom: 4}}>
                            <strong>{label}:</strong> {renderJsonValue(v, options, depth + 1, k)}
                        </div>
                    );
                })}
            </div>
        );
    }

    return <span>{String(value)}</span>;
};
